import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { GalleryDto, GalleryTheme, Section } from '@nusign/picflow-backend-types';
import { Observable, Subject, from, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs';
import { GalleryActivityMenuComponent } from '../../../account/menus/gallery-activity-menu/gallery-activity-menu.component';
import { Rect } from '../../../classes/rect.class';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { GalleryContextMenuComponent } from '../../../gallery-context-menu/gallery-context-menu.component';
import { forkJoinSafe } from '../../../lib/safe-fork-join';
import { sortSubGalleries } from '../../../lib/sort-subgalleries';
import { GalleryUrlService } from '../../../services/gallery-url.service';
import { GalleryService } from '../../../services/gallery.service';
import { SectionService } from '../../../services/section.service';
import { ToastService } from '../../../services/toast.service';
import { ActivityBadgeComponent } from '../../../shared-extra/components/activity-badge/activity-badge.component';
import { GalleryItemComponent } from '../../../shared-extra/components/gallery-item/gallery-item.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { LoadingIndicatorComponent } from '../../../shared/components/loading-indicator/loading-indicator.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { ResizeDirective } from '../../../shared/directives/resize.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { CommonSectionSettingsComponent } from '../common-section-settings/common-section-settings.component';
import { SectionContainerComponent } from '../section-container/section-container.component';

@Component({
  selector: 'app-edit-album-section',
  templateUrl: './edit-album-section.component.html',
  styleUrls: ['./edit-album-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    SectionContainerComponent,
    ResizeDirective,
    ContextMenuComponent,
    ButtonDirective,
    TooltipDirective,
    SvgComponent,
    GalleryItemComponent,
    RouterLink,
    ActivityBadgeComponent,
    GalleryContextMenuComponent,
    LoadingIndicatorComponent,
    TranslatePipe,
    GalleryActivityMenuComponent,
    NgTemplateOutlet,
    CommonSectionSettingsComponent,
    AsyncPipe,
  ],
})
export class EditAlbumSectionComponent implements OnInit, OnDestroy {
  private galleryService = inject(GalleryService);
  private sectionService = inject(SectionService);
  private toasts = inject(ToastService);
  private swapGalleryInUrlService = inject(GalleryUrlService);
  private check = inject(ChangeDetectorRef);
  @ViewChild('sectionContainer') sectionContainer: SectionContainerComponent;
  @ViewChild('sectionContainer', { read: ElementRef }) sectionContainerEl: ElementRef<HTMLElement>;
  @Input() sectionIndex: number;
  @Input() section: Section;
  @Input({ required: true }) theme: GalleryTheme;
  readonly galleryPermissions = input.required<GalleryDto['permissions']>();
  @Input() sections: Section[];
  @Input() parentGallery: GalleryDto;
  @Input() darkBackground: boolean;
  @Output() onMove = new EventEmitter();
  @Output() onMoveStart = new EventEmitter();
  @Output() onMoveDrop = new EventEmitter();
  @Output() addSection: EventEmitter<number> = new EventEmitter();
  galleries$: Observable<GalleryDto[]>;
  legacyToBeRemovedGalleries: GalleryDto[];
  links$: Observable<{ [galleryId: string]: string[] }>;
  addingGallery = signal(false);
  galleryDropHover = signal(false);
  draggingGallery = signal<GalleryDto>(undefined);
  draggingGalleryPos = signal<Rect>(undefined);
  potentialDragging: { gallery: GalleryDto; pos: Rect };
  sortings = ['position', 'created_at', 'title', 'last_changed_at'];
  insertPosition = signal<Rect>(undefined);
  insertIndex: number;
  destroyed$ = new Subject<void>();
  isDesktop = signal(ScreenSizes.screenSize() === 'desktop');
  updateSorting = new Subject<void>();

  @HostListener('window:resize', ['$event']) winResize() {
    this.isDesktop.set(ScreenSizes.screenSize() === 'desktop');
  }

  async ngOnInit() {
    this.galleries$ = this.updateSorting.pipe(
      startWith(null),
      switchMap(() => {
        return this.galleryService.getSectionGalleries(this.section.id, this.parentGallery.id).pipe(
          map(galleries => sortSubGalleries(galleries, this.section.sorting)),
          tap(galleries => (this.legacyToBeRemovedGalleries = galleries))
        );
      }),
      shareReplay(1)
    );

    this.links$ = this.galleries$.pipe(
      map(galleries => {
        const links = {};
        galleries.forEach(
          gallery => (links[gallery.id] = this.swapGalleryInUrlService.getChildPath(this.parentGallery, gallery))
        );
        return links;
      })
    );
  }

  changeGallerySorting(sorting: string) {
    this.section.sorting = sorting;
    this.sectionService.updateSection(this.section, { sorting }).subscribe();
    this.updateSorting.next();
  }

  async addGallery() {
    if (this.addingGallery()) return;
    this.addingGallery.set(true);
    await this.galleryService.createSubGallery(this.parentGallery, this.section.id);
    this.addingGallery.set(false);
  }

  openActivityMenu(event: Event, activityMenu: unknown) {
    event.stopPropagation();
    activityMenu['open']();
    return false;
  }

  mouseDown(event: MouseEvent, gallery: GalleryDto) {
    event.preventDefault();
    this.potentialDragging = { gallery, pos: Rect.fromMouseEvent(event) };
    this.check.detectChanges();
  }

  mouseMove(event: MouseEvent) {
    this.draggingGalleryPos.set(Rect.fromMouseEvent(event));
    if (!event.buttons) {
      if (this.draggingGallery()) this.mouseUp(event);
      return;
    }
    if (this.potentialDragging) {
      const pos = Rect.fromMouseEvent(event);
      const diff = pos.subtract(this.potentialDragging.pos);
      if (diff.magnitude() > 10) {
        this.draggingGallery.set(this.potentialDragging.gallery);
        this.potentialDragging = undefined;
        this.onMoveStart.emit({ gallery: this.draggingGallery(), section: this, event });
        this.check.detectChanges();
      }
    }

    this.sectionContainer.hover = false;
    if (this.draggingGallery()) this.onMove.emit(event);
  }

  mouseUp(event: MouseEvent) {
    this.potentialDragging = undefined;
    if (!this.draggingGallery()) return;
    event.preventDefault();
    this.onMoveDrop.emit(event);
  }

  hideInsertPosition() {
    this.insertPosition.set(undefined);
    this.galleryDropHover.set(false);
    this.check.detectChanges();
  }

  getGalleryBounds() {
    return this.galleries$.pipe(
      take(1),
      map(galleries => {
        const elements = galleries.map(gallery => document.getElementById(`gallery__${gallery.id}`));
        return elements.map(c => c.getBoundingClientRect());
      })
    );
  }

  showInsertPositionBasedOnHover(pos: Rect) {
    this.galleries$.pipe(take(1)).subscribe(galleries => {
      if (!galleries.length) {
        this.galleryDropHover.set(true);
        return;
      }
      return this.getGalleryBounds().subscribe(bounds => {
        const positions = bounds.map(c => new Rect(c.x + c.width / 2, c.y + c.height / 2));
        const distancesXY = positions.map(_pos => pos.subtract(_pos));
        const distances = distancesXY.map(d => d.magnitude());
        const index = distances.indexOf(Math.min(...distances));
        const _distXY = distancesXY[index];

        const sidesX = [
          { side: 'left', dist: _distXY.x },
          { side: 'right', dist: -_distXY.x },
        ].sort((a, b) => (a.dist > b.dist ? 1 : -1));

        const sideX = sidesX[0];
        const newIndex = index + (sideX.side === 'right' ? 1 : 0);
        this.insertIndex = newIndex;

        const parentBounds = Rect.fromDOMRect(this.sectionContainerEl.nativeElement.getBoundingClientRect());
        parentBounds.height = 0;
        parentBounds.width = 0;
        parentBounds.x = 0;

        if (!bounds[newIndex]) {
          const b = bounds[newIndex - 1];
          this.insertPosition.set(new Rect(b.x + b.width + 20, b.y, 0, b.height).subtract(parentBounds));
        } else {
          this.insertPosition.set(
            new Rect(bounds[newIndex].x, bounds[newIndex].y, 0, bounds[newIndex].height).subtract(parentBounds)
          );
        }
      });
    });
  }

  async droppedGallery() {
    this.galleryDropHover.set(false);
    this.draggingGallery.set(undefined);
    const galleries = this.legacyToBeRemovedGalleries;

    const needsUpdating = galleries.filter((g, i) => g.position !== i);

    if (this.section.sorting !== 'position') {
      const sortedGalleries = sortSubGalleries(galleries, this.section.sorting);
      if (sortedGalleries.map(g => g.id).join('') !== galleries.map(g => g.id).join('')) {
        this.section.sorting = 'position';
        this.sectionService.updateSection(this.section, { sorting: 'position' }).subscribe();
        this.toasts.add('Gallery order changed to Manual', 'info');
      }
    }
    this.check.detectChanges();

    forkJoinSafe(
      needsUpdating.map(g => {
        const position = galleries.indexOf(g);
        g.position = position;
        return from(this.galleryService.updateGallery(g.id, { position }));
      })
    )
      .pipe(
        tap(() => {
          this.updateSorting.next();
          this.check.detectChanges();
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
